"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import AnimatedNumber from "@/components/animated-number";
import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname } from "@/lib/region";
import { override, pickSourceContent, type SourcedBlok } from "@/lib/sourced-blok";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";

type Asset = { filename?: string; alt?: string; focus?: string };

function focalCropUrl(
  asset: Asset | undefined,
  width: number,
  height: number,
): string | undefined {
  if (!asset?.filename) return undefined;
  const size = `/m/${width}x${height}`;
  const focal = asset.focus ? `:focal(${asset.focus})` : "";
  const filters = `/filters:quality(90)${focal}`;
  return `${asset.filename}${size}${filters}`;
}

type BigStat = {
  _uid?: string;
  top_label?: string;
  value?: string;
  bottom_label?: string;
};
type BigStatDataStory = {
  content?: { stats?: BigStat[] };
};

type RenderableMetric = {
  _uid: string;
  value?: string;
  label?: string;
};

type SuccessRatesFields = {
  title?: string;
  title_accent?: string;
  intro?: string;
  stats_data?: string | BigStatDataStory;
  max_stats?: string | number;
  image?: Asset;
  desktop_image?: Asset;
  mobile_image_offset_y?: string | number;
  bg_image?: Asset;
  button_label?: string;
  button_link?: StoryblokLink;
  bg_color?: string;
};

type SuccessRatesBlok = SbBlokData & SourcedBlok<SuccessRatesFields>;

export default function SuccessRates({ blok }: { blok: SuccessRatesBlok }) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const src = pickSourceContent(blok);
  const title = override(blok.title, src?.title);
  const titleAccent = override(blok.title_accent, src?.title_accent);
  const intro = override(blok.intro, src?.intro);
  const statsData = override(blok.stats_data, src?.stats_data);
  const maxStats = override(blok.max_stats, src?.max_stats);
  const image = override(blok.image, src?.image);
  const desktopImage = override(blok.desktop_image, src?.desktop_image);
  const mobileImageOffsetY = override(
    blok.mobile_image_offset_y,
    src?.mobile_image_offset_y,
  );
  const bgImage = override(blok.bg_image, src?.bg_image);
  const buttonLabel = override(blok.button_label, src?.button_label);
  const buttonLink = override(blok.button_link, src?.button_link);
  const bgColor = override(blok.bg_color, src?.bg_color);

  const { href, target } = resolveLink(buttonLink, region);
  const { bg } = resolveBg(bgColor, "midnight");
  const showButton = Boolean(buttonLabel && href !== "#");

  // Legacy `bg_image` is the fallback for either side.
  const mobileAsset = image?.filename
    ? image
    : bgImage?.filename
      ? bgImage
      : desktopImage;
  const desktopAsset = desktopImage?.filename
    ? desktopImage
    : bgImage?.filename
      ? bgImage
      : image;
  const mobileSrc = focalCropUrl(mobileAsset, 1290, 1720);
  const desktopSrc = focalCropUrl(desktopAsset, 2560, 1600);
  const hasImage = Boolean(mobileSrc || desktopSrc);
  const mobileOffsetY = Number(mobileImageOffsetY) || 0;
  const mobileTransform = mobileOffsetY
    ? `translateY(${mobileOffsetY}px)`
    : undefined;

  // stats_data is a Big Stat Data story (resolved via resolve_relations).
  const resolvedStats =
    typeof statsData === "object" && statsData
      ? (statsData.content?.stats ?? [])
      : [];
  const limit =
    typeof maxStats === "string"
      ? parseInt(maxStats, 10) || 0
      : maxStats ?? 0;
  const visibleStats = limit > 0 ? resolvedStats.slice(0, limit) : resolvedStats;
  const metrics: RenderableMetric[] = visibleStats.map((s, i) => {
    // Combine bottom_label + top_label: "Pregnancies achieved … under 35 years."
    const parts: string[] = [];
    if (s.bottom_label) parts.push(s.bottom_label.trim());
    if (s.top_label) parts.push(s.top_label.trim().toLowerCase());
    const sentence = parts.join(" ");
    const label = sentence
      ? sentence.endsWith(".")
        ? sentence
        : `${sentence}.`
      : undefined;
    return {
      _uid: s._uid ?? `mapped-${i}`,
      value: s.value,
      label,
    };
  });

  return (
    <section
      {...storyblokEditable(blok)}
      className={`relative isolate overflow-hidden text-white ${hasImage ? "" : bg}`}
    >
      {mobileSrc && mobileAsset && (
        <div
          className={`absolute -z-20 md:hidden ${mobileOffsetY ? "-inset-24" : "inset-0"}`}
          style={mobileTransform ? { transform: mobileTransform } : undefined}
        >
          <Image
            src={mobileSrc}
            alt={mobileAsset.alt || ""}
            fill
            sizes="(min-width: 768px) 0px, 100vw"
            className="object-cover"
            priority={false}
          />
        </div>
      )}
      {desktopSrc && desktopAsset && (
        <div className="absolute inset-0 -z-20 hidden md:block">
          <Image
            src={desktopSrc}
            alt={desktopAsset.alt || ""}
            fill
            sizes="(min-width: 768px) 100vw, 0px"
            className="object-cover"
            priority={false}
          />
        </div>
      )}
      {hasImage && (
        <div
          aria-hidden
          className="absolute inset-0 -z-10 mix-blend-multiply"
          style={{
            backgroundImage:
              "linear-gradient(89.7deg, rgb(51, 55, 47) 0%, rgba(121, 129, 119, 0.05) 100%)",
          }}
        />
      )}

      <div className="mx-auto w-full max-w-[1280px] px-5 py-10 lg:px-[clamp(1.25rem,4vw,2.5rem)] lg:py-[clamp(3.75rem,7vw,7.25rem)] lg:pb-[190px]">
        <div className="flex w-full flex-col gap-[clamp(2rem,3.5vw,3rem)]">
          <div className="flex flex-col gap-5 lg:max-w-[468px]">
            {(title || titleAccent) && (
              <h2 className="text-[clamp(2.25rem,4vw,3rem)] font-bold leading-[1.08]">
                {title && <span className="block text-white">{title}</span>}
                {titleAccent && (
                  <span className="block text-purple-01">{titleAccent}</span>
                )}
              </h2>
            )}
            {intro && (
              <p className="text-body leading-[1.64] lg:leading-normal">
                {intro}
              </p>
            )}
            {showButton && (
              <a
                href={href}
                {...(target ? { target } : {})}
                className="inline-flex h-12 w-fit items-center justify-center rounded-lg border border-white px-5 text-body font-bold text-white transition-colors hover:bg-white/10 lg:h-14 lg:px-7"
              >
                {buttonLabel}
              </a>
            )}
          </div>

          {metrics.length > 0 && (
            <div className="grid w-full grid-cols-1 gap-[14px] [@media(min-width:560px)]:grid-cols-2 [@media(min-width:768px)]:grid-cols-3 lg:gap-4">
              {metrics.map((m) => (
                <div
                  key={m._uid}
                  {...storyblokEditable(m)}
                  className="flex h-[188px] w-full flex-col gap-[5px] rounded-[13px] bg-forest-03 p-[22px] lg:h-[230px] lg:w-auto lg:gap-[6px] lg:rounded-[16px] lg:p-[27px]"
                >
                  <div className="text-[73px] font-bold leading-[1.054] tracking-[0.02em] text-purple-01 lg:text-[90px]">
                    <AnimatedNumber value={m.value} />
                  </div>
                  {m.label && (
                    <div className="text-button font-medium leading-[1.3] text-white lg:text-body lg:font-semibold">
                      {m.label}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
